import SelectState from 'app/components/forms/SelectState';
import ChevronDown from 'app/components/svgs/ChevronDown';
import {
  GREY_B,
  GREY_S,
  GREY_U,
  WHITE_A,
  RED_G,
} from 'app/constants/Colors';
import {
  BORDER_RADIUS_B,
  BOX_SHADOW_A,
} from 'app/constants/Styles';
import { createStyles } from 'app/utils/css';
import { List } from 'immutable';
import React, { ReactNode } from 'react';
import { inputBaseStyles, inputHeight, inputHeightPx } from 'app/components/presentation/forms/styles';

interface IProps {
  optionIds: List<string>;
  optionLabels?: List<string>;
  selectedId: string | undefined;
  onSelect: (optionId: string) => void;
  renderOption?: (optionId: string | undefined) => ReactNode;
  renderSelectedOption?: (optionId: string | undefined) => ReactNode;
  error?: boolean;
  labelId?: string;
  labelRef?: React.RefObject<HTMLLabelElement>;
}

const Select: React.SFC<IProps> = ({
  optionIds,
  optionLabels,
  selectedId,
  onSelect,
  renderOption = (id: string | undefined) => id,
  renderSelectedOption = renderOption,
  error = false,
  labelId = '',
  labelRef,
}) => (
  <SelectState
    optionIds={optionIds}
    optionLabels={optionLabels}
    selectedId={selectedId}
    onSelect={onSelect}
  >{({ isOpen, SelectWrapper, Option }) => (
    <Styles attributes={{ isOpen, error }}>{(css) =>
      <SelectWrapper labelRef={labelRef}>{(wrapperElementProps) => (
        <div {...wrapperElementProps} className={css('wrapper')} aria-labelledby={labelId}>
          <div className={css('selected')}>
            <div className={css('selectedText')}>
              {renderSelectedOption(selectedId)}
            </div>

            <div className={css('arrow')}>
              <ChevronDown width="9px" height="9px" strokeWidth="5" color={GREY_B} />
            </div>
          </div>

          <div className={css('dropdown')}>
            {optionIds.map((id) =>
              <Option key={id} id={id}>{(optionElementProps) => (
                <div className={css('option')} {...optionElementProps}>
                  {renderOption(id)}
                </div>
              )}
              </Option>,
            )}
          </div>
        </div>
      )}
      </SelectWrapper>
    }</Styles>
  )}
  </SelectState>
);

const contentPadding = '2px 10px';

const Styles = createStyles({
  wrapper: ({ isOpen, error }) => [
    {
      ...inputBaseStyles,
      position: 'relative',
      cursor: 'pointer',
      lineHeight: '17px',
    },

    isOpen && {
      border: `1px solid ${GREY_S}`,
      borderBottomColor: GREY_U,
      borderBottomRightRadius: '0',
      borderBottomLeftRadius: '0',
    },

    error && {
      borderColor: RED_G,
    },
  ],
  dropdown: ({ isOpen }) => [
    {
      // WARNING: Because the dropdown contents are either hidden or absolutely positioned, they do
      // not contribute to the width of the select menu unlike a native <select> element, which can
      // cause the width to be too small for the options. You may need to set an appropriate minimum
      // width to prevent text wrapping in the options.
      //
      // TODO: Figure out how to make this behave more like a native <select> element.
      position: 'absolute',
      left: '-1px',
      right: '-1px',
      top: '100%',
      zIndex: 1,

      backgroundColor: 'white',
      boxShadow: BOX_SHADOW_A,
      border: `1px solid ${GREY_S}`,
      borderTopColor: GREY_U,
      borderRadius: BORDER_RADIUS_B,
      borderTopRightRadius: '0',
      borderTopLeftRadius: '0',

      maxHeight: `${inputHeightPx * 10}px`,
      overflowY: 'auto',
      overflowX: 'visible',
    },

    !isOpen && {
      display: 'none',
    },
  ],
  option: {
    padding: contentPadding,
    minHeight: inputHeight,
    display: 'flex',
    alignItems: 'center',
    ':focus': {
      outline: 'none',
      backgroundColor: WHITE_A,
    },
  },
  selected: {
    display: 'flex',
    alignItems: 'center',
    minHeight: inputHeight,
  },
  selectedText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flexGrow: 1,
    padding: contentPadding,
  },
  arrow: {
    padding: contentPadding,
  },
});

export default Select;
